import { CATEGORIES_QUERY_PARAM } from '@shared/system-constants';
import { ISearchCategoryFilter } from '@shared/types/search';
import { Collection } from '@unified-commerce/gpc-vue-storefront-shopify';
import { CollectionPage, Thing, WithContext } from 'schema-dts';

import { IVehicleFacet } from '~/composables/useBikeVehicleFacets';
import {
  BIKE_CATEGORY_HANDLE_KEYWORDS,
  BIKE_CATEGORY_KEYWORD_HANDLES,
} from '~/config/bikeCategoryKeywords';
import {
  PAGE_QUERY_PARAM,
  PER_PAGE_QUERY_PARAM,
  SEARCH_CATEGORY_PATH_SEPERATOR,
  SEARCH_CATEGORY_TENANT_PREFIX_HANDLE,
  SEARCH_CATEGORY_TENANT_PREFIX_TITLE,
  SORT_BY_QUERY_PARAM,
} from '~/constants';

/**
 * Slugifies a facet value for use in a URL. Deliberately not `lodash/kebabCase`:
 * kebabCase splits on letter/digit boundaries (e.g. `crf450r` -> `crf-450-r`,
 * `cbr1000rr fireblade` -> `cbr-1000-rr-fireblade`), which breaks real alphanumeric
 * model codes. Facet values are already lowercase, space-separated tokens, so we
 * only need to replace whitespace with hyphens.
 */
export const slugifyFacetValue = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, '-');

export interface IBikeBreadcrumb {
  text: string;
  link: string;
}

export const getMakeBreadcrumbs = (make: string, slug: string): IBikeBreadcrumb[] => [
  { text: 'Home', link: '/' },
  { text: 'Bikes', link: '/bikes' },
  { text: make, link: `/bikes/${slug}` },
];

const NARROW_DOWN_LABEL = 'Narrow it down to your exact bike';

/**
 * `null` means the product search hasn't resolved yet. It's kept distinct from
 * `0` so copy never claims "0 parts" for a bike that's still loading.
 */
export type PartCount = number | null;

const stocksPhrase = (partCount: PartCount) =>
  partCount === null ? 'AMX stocks parts' : `AMX stocks ${partCount} parts`;

export const getMakePlateProps = (
  make: string,
  partCount: PartCount,
  submodels: IVehicleFacet[],
) => ({
  title: make,
  kicker: 'Motorcycle parts & maintenance',
  childLabel: submodels.length
    ? `${submodels.length} ${submodels.length === 1 ? 'model' : 'models'} to choose from`
    : '',
  selectorLabel: NARROW_DOWN_LABEL,
  partCount,
  partCountLabel: `parts confirmed to fit a ${make}`,
  blurb: `Every part here is matched against confirmed ${make} fitment data.`,
});

export const getMakeSeoCopy = (make: string, partCount: PartCount): string =>
  `<p>${stocksPhrase(
    partCount,
  )} and maintenance items confirmed to fit ${make} motorcycles — brake pads, chain and sprocket kits, tyres, levers, exhausts, engine oil, batteries and more — each matched against ${make} fitment data.</p><p>Fitment on this page is shown at the make level: a part listed here is confirmed for at least one ${make} model, not necessarily all of them.</p>`;

export const getSubmodelBreadcrumbs = (
  make: string,
  makeSlug: string,
  submodel: string,
  modelSlug: string,
): IBikeBreadcrumb[] => [
  ...getMakeBreadcrumbs(make, makeSlug),
  { text: submodel, link: `/bikes/${makeSlug}/${modelSlug}` },
];

export const getSubmodelPlateProps = (
  make: string,
  submodel: string,
  partCount: PartCount,
  years: number[],
) => ({
  title: `${make} ${submodel}`,
  kicker: 'Motorcycle parts & maintenance',
  childLabel: years.length
    ? `${years.length} ${years.length === 1 ? 'year' : 'years'} to choose from`
    : '',
  selectorLabel: NARROW_DOWN_LABEL,
  partCount,
  partCountLabel: `parts confirmed to fit a ${make} ${submodel}`,
  blurb: `Every part here is matched against confirmed ${make} ${submodel} fitment data.`,
});

export const getSubmodelSeoCopy = (make: string, submodel: string, partCount: PartCount): string =>
  `<p>${stocksPhrase(
    partCount,
  )} and maintenance items confirmed to fit ${make} ${submodel} motorcycles — brake pads, chain and sprocket kits, tyres, levers, exhausts, engine oil, batteries and more — each matched against ${make} ${submodel} fitment data.</p><p>Fitment on this page is shown at the model level: a part listed here is confirmed for at least one year of the ${make} ${submodel}, not necessarily every year.</p>`;

export const getYearBreadcrumbs = (
  make: string,
  makeSlug: string,
  submodel: string,
  modelSlug: string,
  year: number,
): IBikeBreadcrumb[] => [
  ...getSubmodelBreadcrumbs(make, makeSlug, submodel, modelSlug),
  { text: `${year}`, link: `/bikes/${makeSlug}/${modelSlug}/${year}` },
];

export const getYearPlateProps = (
  make: string,
  submodel: string,
  year: number,
  partCount: PartCount,
  years: number[],
) => ({
  ...getSubmodelPlateProps(make, submodel, partCount, years),
  title: `${make} ${submodel} ${year}`,
  selectorLabel: 'Your bike',
  partCountLabel: `parts confirmed to fit a ${make} ${submodel} (${year})`,
  blurb: `Every part here is matched against confirmed ${make} ${submodel} ${year} fitment data.`,
});

/**
 * Storyblok bike content lives as a single flat slug under the `content/bikes/`
 * folder — e.g. `content/bikes/honda-cbr1000rr-fireblade-2020-chain-guide` — not
 * nested per make/model/year. Built from the route only — query filters such as
 * `style` never contribute a segment.
 */
export const buildBikeContentSlug = (
  makeSlug: string,
  modelSlug: string | undefined,
  yearSlug: string | undefined,
  categoryKeyword?: string,
): string => {
  const segments = [makeSlug, modelSlug, yearSlug, categoryKeyword].filter(Boolean);
  return `content/bikes/${segments.join('-')}`;
};

export const toVehicleIdOptions = (
  vehicles: { vehicleId: number }[],
): { title: string; value: string }[] =>
  vehicles.map((vehicle) => ({ title: `${vehicle.vehicleId}`, value: `${vehicle.vehicleId}` }));

export const getYearSeoCopy = (
  make: string,
  submodel: string,
  year: number,
  partCount: PartCount,
): string =>
  `<p>${stocksPhrase(
    partCount,
  )} and maintenance items confirmed to fit a ${year} ${make} ${submodel} — brake pads, chain and sprocket kits, tyres, levers, exhausts, engine oil, batteries and more — each matched against exact ${year} ${make} ${submodel} fitment data.</p><p>Fitment on this page is shown at the exact year level: every part listed here is confirmed for the ${year} ${make} ${submodel} specifically.</p>`;

export const getBikeMetaDescription = (subject: string, partCount: PartCount): string =>
  `Shop ${
    partCount === null ? '' : `${partCount} `
  }motorcycle parts and maintenance items confirmed to fit ${subject} motorcycles. Free shipping Australia wide, Zip Pay and Afterpay available.`;

/** The URL keyword for a category handle, if it has a page. */
export const getBikeCategoryKeyword = (handle: string): string | undefined =>
  BIKE_CATEGORY_HANDLE_KEYWORDS.get(handle);

/**
 * Model and year pages take the category's SEO keyword as the last path
 * segment — each bike + category pair is its own page, and the path is its
 * canonical. Make pages have no category pages (fitment is too loose at that
 * level), and categories with no mapped keyword have no page either, so both
 * keep the category as a query filter.
 */
export const getBikeCategoryLink = (
  bikePath: string,
  handle: string,
  { inPath }: { inPath: boolean },
): string => {
  const keyword = inPath ? getBikeCategoryKeyword(handle) : undefined;
  return keyword
    ? `${bikePath}/${keyword}`
    : `${bikePath}?${CATEGORIES_QUERY_PARAM}=${encodeURIComponent(handle)}`;
};

export interface IBikeChildLink {
  text: string;
  to: string;
}

export interface IBikeChildLinks {
  heading: string;
  links: IBikeChildLink[];
}

/**
 * Storyblok `SearchLink` bloks (title + multilink) from the bike story's
 * related/popular searches, as plain links. Story links resolve via
 * `cached_url`; URL links are kept only when site-relative, since the links
 * render as in-app routes. Targets are bike page paths; editors enter them
 * without the `/bikes` prefix (e.g. `/yamaha/xsr-700/tool-kit`), so it's added
 * when missing. Anything without a title or a usable target is dropped.
 */
export const getBikeSearchLinks = (blocks: unknown): IBikeChildLink[] => {
  if (!Array.isArray(blocks)) return [];

  return blocks.flatMap((block): IBikeChildLink[] => {
    const text = typeof block?.title === 'string' ? block.title.trim() : '';
    const link = block?.link;
    const target = link?.linktype === 'story' ? link.cached_url : link?.url || link?.cached_url;
    if (!text || typeof target !== 'string' || !target) return [];
    if (/^[a-z][a-z\d+.-]*:|^\/\//i.test(target)) return [];

    const path = target.startsWith('/') ? target : `/${target}`;
    return [{ text, to: /^\/bikes(\/|$)/.test(path) ? path : `/bikes${path}` }];
  });
};

/**
 * Plain downward links (make -> models, model -> years, year -> sibling years).
 * The plate's dropdowns aren't crawlable, so these keep every bike page
 * reachable by internal links.
 */
export const getBikeChildLinks = ({
  make,
  makeSlug,
  submodels,
  submodel,
  modelSlug,
  years,
  year,
}: {
  make: string;
  makeSlug: string;
  submodels: IVehicleFacet[];
  submodel?: string;
  modelSlug?: string;
  years: number[];
  year?: number;
}): IBikeChildLinks => {
  if (!submodel || !modelSlug) {
    return {
      heading: `All ${make} models`,
      links: submodels.map((facet) => ({
        text: facet.title,
        to: `/bikes/${makeSlug}/${slugifyFacetValue(facet.value)}`,
      })),
    };
  }

  return {
    heading: year ? `Other ${make} ${submodel} years` : `All ${make} ${submodel} years`,
    links: years
      .filter((candidate) => candidate !== year)
      .map((candidate) => ({
        text: `${candidate}`,
        to: `/bikes/${makeSlug}/${modelSlug}/${candidate}`,
      })),
  };
};

export interface IBikeEmptyState {
  message: string;
  links: IBikeChildLink[];
}

const CONTACT_LINK: IBikeChildLink = { text: 'Ask our fitment team', to: '/pages/contact' };

/**
 * Shown when a bike genuinely has no confirmed parts: says so plainly and
 * offers a step one level up the make/model/year tree, plus a way to get help.
 */
export const getBikeEmptyState = ({
  make,
  makeSlug,
  submodel,
  modelSlug,
  year,
}: {
  make: string;
  makeSlug: string;
  submodel?: string;
  modelSlug?: string;
  year?: number;
}): IBikeEmptyState => {
  if (!submodel || !modelSlug) {
    return {
      message: `We don't have parts confirmed to fit ${make} motorcycles yet.`,
      links: [{ text: 'Browse other makes', to: '/bikes' }, CONTACT_LINK],
    };
  }

  if (!year) {
    return {
      message: `We don't have parts confirmed to fit the ${make} ${submodel} yet.`,
      links: [{ text: `Shop all ${make} parts`, to: `/bikes/${makeSlug}` }, CONTACT_LINK],
    };
  }

  return {
    message: `We don't have parts confirmed to fit the ${year} ${make} ${submodel} yet.`,
    links: [
      { text: `Shop all ${make} ${submodel} parts`, to: `/bikes/${makeSlug}/${modelSlug}` },
      CONTACT_LINK,
    ],
  };
};

const NON_FILTER_QUERY_PARAMS = [PAGE_QUERY_PARAM, PER_PAGE_QUERY_PARAM, SORT_BY_QUERY_PARAM];

/** Whether the listing is narrowed by a filter, as opposed to just paged or sorted. */
export const hasActiveListingFilters = (query: Record<string, unknown>): boolean =>
  Object.keys(query).some((key) => !NON_FILTER_QUERY_PARAMS.includes(key));

export interface IBikeCategoryTile {
  title: string;
  count: number;
  to: string;
  active: boolean;
}

// Merchandising trees, not product types — they'd duplicate every tile.
const EXCLUDED_CATEGORY_PREFIXES = ['brand-categories', 'promotions'];
const MIN_CATEGORY_TILES = 3;
const MAX_CATEGORY_TILES = 12;
// A tile holding more than this share of the parts is too broad to help.
const DOMINANT_CATEGORY_SHARE = 0.5;

export interface IBikeCategory {
  /** Handle without the tenant prefix, as the `categories` search filter takes it. */
  handle: string;
  title: string;
}

const isExcludedCategory = (handle: string) =>
  EXCLUDED_CATEGORY_PREFIXES.some((prefix) => handle.startsWith(prefix));

/**
 * The Shopify category a bike page's path segment names. The segment is the
 * category's SEO keyword, which only the mapping knows how to resolve — an
 * unmapped keyword has no page.
 */
export const findBikeCategory = (
  collections: readonly Pick<Collection, 'handle' | 'title'>[],
  keyword: string,
): IBikeCategory | undefined => {
  const handle = BIKE_CATEGORY_KEYWORD_HANDLES.get(keyword);
  if (!handle || isExcludedCategory(handle)) return undefined;
  const collection = collections.find(
    (candidate) => candidate.handle === `${SEARCH_CATEGORY_TENANT_PREFIX_HANDLE}${handle}`,
  );
  if (!collection?.title.startsWith(SEARCH_CATEGORY_TENANT_PREFIX_TITLE)) return undefined;
  return {
    handle,
    title: collection.title.split(SEARCH_CATEGORY_PATH_SEPERATOR).pop() ?? '',
  };
};

/**
 * Category tiles for the bike's own category facets. The top of the tree is
 * too broad to be useful (nearly everything is "Parts & Maintenance > Hard
 * Parts"), so the biggest category is replaced by its children until there
 * are several tiles and none holds most of the parts — e.g. Chain & Sprockets,
 * Wheels & Tyres, Brakes, Maintenance. Category facet counts ignore the
 * selected category, so the tiles stay put and the selection is marked active.
 * A category `linkTo` gives no link (no page of its own) is replaced by its
 * descendants that have one, so only linkable categories become tiles.
 */
export const getBikeCategoryTiles = (
  categoryFilters: ISearchCategoryFilter[],
  linkTo: (handle: string) => string | undefined,
  selectedHandle = '',
): IBikeCategoryTile[] => {
  const categories = categoryFilters.filter(
    (category) => !isExcludedCategory(category.exactHandle),
  );
  const childrenOf = (handle: string | null) =>
    categories.filter(
      (category) => (category.ancestors[category.ancestors.length - 1] ?? null) === handle,
    );

  let level = childrenOf(null);
  for (;;) {
    const [largest] = level
      .filter((category) => childrenOf(category.exactHandle).length)
      .sort((a, b) => b.productCount - a.productCount);
    if (!largest) break;

    const total = level.reduce((sum, category) => sum + category.productCount, 0);
    const tooBroad = largest.productCount > total * DOMINANT_CATEGORY_SHARE;
    if (level.length >= MIN_CATEGORY_TILES && !tooBroad) break;

    level = level.flatMap((category) =>
      category === largest ? childrenOf(category.exactHandle) : [category],
    );
  }

  const toTiles = (category: ISearchCategoryFilter): IBikeCategoryTile[] => {
    const to = linkTo(category.exactHandle);
    if (!to) return childrenOf(category.exactHandle).flatMap(toTiles);
    return [
      {
        title: category.exactTitle,
        count: category.productCount,
        to,
        active: category.exactHandle === selectedHandle,
      },
    ];
  };

  return level
    .flatMap(toTiles)
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_CATEGORY_TILES);
};

// Brand spellings the word-by-word rule below can't infer.
const MAKE_NAME_OVERRIDES: Record<string, string> = { GASGAS: 'GasGas' };

const formatMakeWord = (word: string): string =>
  // No vowels means an acronym (BMW, KTM, SWM, CF) — keep it as-is.
  /[aeiouy]/i.test(word) ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : word;

/**
 * Make facet values are all caps (`HONDA`, `HARLEY-DAVIDSON`); this gives the
 * display form used in titles and copy. Only for display — search facet
 * filters need the raw value.
 */
export const formatMakeName = (make: string): string =>
  MAKE_NAME_OVERRIDES[make] ??
  make
    .split(' ')
    .map((word) => word.split('-').map(formatMakeWord).join('-'))
    .join(' ');

export interface IBikeCollectionItem {
  name: string;
  url: string;
}

/**
 * Parses the bike story's `structured_data` field — raw schema.org JSON-LD
 * pasted by an editor, as one object or an array of them. Anything malformed
 * (bad JSON, no `@context`/`@type`) is dropped rather than shipped to the head.
 */
export const parseBikeStructuredData = (raw: unknown): WithContext<Thing>[] => {
  if (typeof raw !== 'string' || !raw.trim()) return [];

  // Editors sometimes paste the whole <script type="application/ld+json"> tag.
  const json = raw.replace(/^\s*<script[^>]*>/i, '').replace(/<\/script>\s*$/i, '');

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }

  return (Array.isArray(parsed) ? parsed : [parsed]).filter(
    (schema): schema is WithContext<Thing> =>
      !!schema && typeof schema === 'object' && !!schema['@context'] && !!schema['@type'],
  );
};

/** schema.org CollectionPage whose main entity lists the products on the page. */
export const buildBikeCollectionPageSchema = ({
  name,
  description,
  url,
  items,
}: {
  name: string;
  description: string;
  url: string;
  items: IBikeCollectionItem[];
}): WithContext<CollectionPage> => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  url,
  ...(items.length && {
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url: item.url,
      })),
    },
  }),
});
